<template>
  <div class="relative w-full h-full">
    <!-- Manual Survey Mode View -->
    <div v-if="isManualSurveyMode" id="manual-survey-container" class="container mx-auto z-0 overflow-hidden flex flex-col h-[calc(100vh-220px)]">
      <div class="flex items-center justify-between p-4 bg-blue-50 border-b border-blue-200">
        <div class="flex items-center">
          <svg class="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
          </svg>
          <div>
            <h3 class="text-sm font-medium text-blue-900">{{ t('modals.kestrel_map.survey_mode_active') }}</h3>
            <p class="text-sm text-blue-700">{{ t('modals.kestrel_map.cluster') }}: {{ surveyBatchName }}</p>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="submitManualSurveys"
            class="px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors duration-200 shadow-sm"
          >
            {{ t('modals.kestrel_map.submit_surveys') }}
          </button>
          <button
            @click="toggleManualSurveyMode"
            class="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors duration-200"
          >
            {{ t('modals.kestrel_map.exit_manual_survey_mode') }}
          </button>
        </div>
      </div>
      <survey-batch-kestrel-surveying
        :preselected-villages="preselectedVillageCodes"
        :target-area-id="currentTargetAreaId"
        :batch-uuid="surveyBatchUuid"
        :country-code="props.target_area_data[0].country_iso3_code"
        :country-id="props.target_area_data[0].country_id"
        :kestrel-api-key="props.target_area_data[0].kestrel_api_key"
        :kestrel-api-url="props.target_area_data[0].kestrel_api_url"
        :center-lat="center.lat"
        :center-lng="center.lng"
        :static-mode="false"
        :batch-data="manualSurveyBatchData"
        @batch-created="handleManualSurveyBatchCreated"
      />
    </div>
    <!-- END Manual Survey Mode View -->

    <!-- Main Map View -->
    <div class="container mx-auto z-0 overflow-hidden flex flex-col h-auto md:h-[calc(100vh-220px)]" :class="!isManualSurveyMode ? '' : 'max-h-0'">
      <!-- Pending Surveys Alert Banner -->
      <div v-if="hasPendingSurveys && !isManualSurveyMode" class="px-4 py-3 bg-amber-50 border-b border-amber-200">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <svg class="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
            <div>
              <h3 class="text-sm font-medium text-amber-900">{{ t('modals.kestrel_map.pending_surveys_alert_title') }}</h3>
              <p class="text-sm text-amber-700">{{ t('modals.kestrel_map.pending_surveys_alert_message', { count: pendingSurveysCount }) }}</p>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="toggleManualSurveyMode"
              class="px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors duration-200"
            >
              {{ t('modals.kestrel_map.continue_surveying') }}
            </button>
            <button
              @click="clearPendingSurveys"
              class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors duration-200"
            >
              {{ t('modals.kestrel_map.discard_surveys') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Header for both normal-view and modal-view modes -->
      <div class="px-4 flex flex-col md:flex-row md:justify-between md:items-center" :class="props.viewOnly ? 'py-2 md:py-4 border-b border-gray-200 bg-gray-50' : 'pb-4 md:pb-4'">
        <!-- Title section - only show in modal-view mode -->
        <div v-if="props.viewOnly" class="flex flex-col md:flex-row md:items-center md:space-x-3">
          <h2 class="text-lg md:text-xl font-semibold text-gray-900 leading-tight">{{ t('modals.view_only_target_area.target_area') }}</h2>
          <span class="text-xs md:text-sm text-gray-500 mt-1 md:mt-0">{{ props.target_area_data[0]?.name || 'Training Center' }}</span>
        </div>
        <!-- Empty div for non-view-only mode to maintain layout -->
        <div v-else class="flex flex-col md:flex-row md:items-center md:space-x-3">
        </div>

        <!-- Buttons section -->
        <div class="flex items-center space-x-2 mt-2 md:mt-0">
          <button
            v-if="!isManualSurveyMode && isEditing && !hasPendingSurveys"
            @click="toggleManualSurveyMode"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 shadow-sm"
            title="Manually Enter Surveys"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
            </svg>
            <span class="hidden md:inline">{{ t('modals.kestrel_map.manual_survey') }}</span>
          </button>
          <!-- Secure PDF checkbox -->
          <label
            v-if="!isManualSurveyMode && isEditing && !hasPendingSurveys"
            class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 cursor-pointer"
            :title="t('modals.kestrel_map.secure_pdf_tooltip')"
          >
            <input
              type="checkbox"
              v-model="generateSecurePdf"
              class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <svg class="ml-1.5 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
            <span class="ml-1 hidden md:inline">{{ t('modals.kestrel_map.secure_pdf') }}</span>
          </label>
          <!-- PDF Generation button -->
          <button
            v-if="!isManualSurveyMode && isEditing && !hasPendingSurveys"
            @click="generatePDF"
            :disabled="isPdfGenerating"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors duration-200 shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            title="Generate PDF"
          >
            <svg v-if="!isPdfGenerating" class="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="white" stroke="none">
              <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z"/>
            </svg>
            <svg v-else class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="hidden md:inline">{{ isPdfGenerating ? t('modals.kestrel_map.generating_pdf') : t('modals.kestrel_map.generate_pdf') }}</span>
          </button>
          <!-- Edit button - only show in modal-view mode -->
          <button
            v-if="props.viewOnly"
            @click="editTargetArea"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md transition-colors duration-200 shadow-sm"
            title="Edit Target Area"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            <span class="hidden md:inline">{{ t('modals.view_only_target_area.edit_target_area') }}</span>
          </button>
        </div>
      </div>

      <!-- Mobile Tab Bar -->
      <div class="block md:hidden border-b border-gray-200 bg-white">
        <nav class="flex">
          <button
            class="flex-1 py-2 px-4 text-center text-sm font-medium focus:outline-none transition-colors duration-150"
            :class="activeMobileTab === 'map' ? 'border-b-2 border-blue-600 text-blue-600 bg-gray-50' : 'text-gray-600'"
            @click="activeMobileTab = 'map'"
          >
            {{ t('modals.kestrel_map.map_view') }}
          </button>
          <button
            class="flex-1 py-2 px-4 text-center text-sm font-medium focus:outline-none transition-colors duration-150"
            :class="activeMobileTab === 'villages' ? 'border-b-2 border-blue-600 text-blue-600 bg-gray-50' : 'text-gray-600'"
            @click="activeMobileTab = 'villages'"
          >
            {{ t('modals.kestrel_map.village_details') }}
          </button>
          <button
            v-if="isEditing && !isManualSurveyMode"
            class="flex-1 py-2 px-4 text-center text-sm font-medium focus:outline-none transition-colors duration-150"
            :class="activeMobileTab === 'clusters' ? 'border-b-2 border-blue-600 text-blue-600 bg-gray-50' : 'text-gray-600'"
            @click="activeMobileTab = 'clusters'"
          >
            {{ t('modals.survey_batches.survey_clusters') }}
          </button>
        </nav>
      </div>

      <div class="flex flex-col md:flex-row gap-4" :style="isMobile ? 'height: calc(100vh - 220px);' : 'height: calc(100vh - 300px);'">
        <!-- Village Details Panel -->
        <div
          class="w-full md:w-1/3 flex flex-col justify-start items-stretch transition-all duration-300"
          :class="isMobile ? (activeMobileTab === 'villages' ? 'flex' : 'hidden') : 'flex'"
          :style="isMobile && activeMobileTab === 'villages' ? 'height: calc(100vh - 220px);' : ''"
        >
          <!-- Village Details -->
          <div v-if="!isMobile || activeMobileTab === 'villages'" class="h-full flex flex-col border border-gray-100 bg-gray-50" style="min-height: 0;">
            <div class="px-4 py-1 bg-gray-200 border-b border-gray-200 flex-shrink-0">
              <h2 class="text-lg font-semibold text-gray-800">{{ t('modals.kestrel_map.village_details') }}</h2>
            </div>

            <!-- Village List -->
            <div class="flex-1 overflow-y-auto">
              <div v-if="selectedVillages.length > 0" class="divide-y divide-gray-200">
                <div
                  v-for="village in sortedVillages"
                  :key="village.villageAdmin4Code"
                  @mouseenter="handleVillageHoverIn(village)"
                  @mouseleave="handleVillageHoverOut(village)"
                  class="p-2 transition-colors duration-150 relative hover:bg-gray-50"
                >
                  <div class="flex flex-col space-y-2">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-2">
                        <div class="flex items-center justify-center mr-2">
                          <span class="circle" :style="{ backgroundColor: village.villageShadingColor }">
                            <span class="circle-number">{{ village.index }}</span>
                          </span>
                        </div>

                        <div class="flex flex-col">
                          <h3
                            class="text-base font-medium text-gray-900 transition-colors duration-200 cursor-pointer hover:text-blue-600"
                            :title="t('modals.kestrel_map.admin_code') + ': ' + village.villageAdmin4Code"
                            @click.stop="copyToClipboard(village.villageAdmin4Code)"
                          >
                            <span>{{ village.villageName }}</span>
                            <span v-if="copiedCode === village.villageAdmin4Code" class="ml-2 text-xs text-green-600">
                              {{ t('modals.kestrel_map.copied') }}
                            </span>
                          </h3>
                          <div class="flex items-center gap-1 mt-1">
                            <span class="text-xs text-gray-500">{{ t('modals.kestrel_map.population') }}:</span>
                            <span class="text-xs font-semibold text-gray-700">{{ village.villagePopulation?.toLocaleString() || 'N/A' }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="flex items-center justify-center h-full text-gray-500 p-4">
                {{ t('modals.kestrel_map.no_villages_in_target_area') }}
              </div>
            </div>

            <div class="px-4 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0">
              <div class="flex items-center justify-between">
                <!-- Village count -->
                <div class="text-xs text-gray-500">
                  {{ t('modals.kestrel_map.total_villages', { count: selectedVillages.length }) }}
                </div>

                <!-- Sort buttons -->
                <div class="flex items-center space-x-2">
                  <button
                    @click="handleSort('index')"
                    class="flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors duration-200"
                    :class="sortField === 'index' ? 'bg-gray-100 text-gray-800' : ''"
                    title="Sort by Village Number"
                  >
                    <span class="mr-1">#</span>
                    <span>{{ getSortIcon('index') }}</span>
                  </button>
                  <button
                    @click="handleSort('name')"
                    class="flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors duration-200"
                    :class="sortField === 'name' ? 'bg-gray-100 text-gray-800' : ''"
                    title="Sort by Village Name"
                  >
                    <span class="mr-1">ABC</span>
                    <span>{{ getSortIcon('name') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Survey Clusters Panel (Mobile Only) -->
        <div
          v-if="isEditing"
          class="w-full md:hidden flex flex-col justify-start items-stretch transition-all duration-300"
          :class="isMobile ? (activeMobileTab === 'clusters' ? 'block' : 'hidden') : 'hidden'"
          style="height: calc(100vh - 220px);"
        >
          <survey-batches
            :target-area-external-id="currentTargetAreaId || props.target_area_data[0].target_area_external_id"
            :training-center-name="props.target_area_data[0].name"
            :training-center-id="props.target_area_data[0].id"
            :preselected-villages="preselectedVillageCodes"
            :view-only="props.viewOnly"
            :mobile-view="true"
          />
        </div>

        <!-- Map Container (always mounted) -->
        <div
          class="w-full md:w-2/3 md:flex flex-col transition-all duration-300 md:h-full"
          :class="isMobile ? (activeMobileTab === 'map' ? 'block' : 'hidden') : 'flex'"
        >
          <!-- Map -->
          <div id="map-container-to-print" class="flex-1 relative min-h-[400px] md:min-h-0">
            <div id="kestrel-map-container" ref="kestrelMapContainer" class="absolute inset-0 border-4 border-l-2 border-gray-300 rounded-lg shadow-lg"></div>
          </div>
          <!-- Map Controls -->
          <div class="flex flex-row justify-between items-center p-4 bg-white border-b border-gray-200">
            <!-- Radius Slider -->
            <div class="flex items-center space-x-4">
              <div v-if="!isEditing && !props.viewOnly" class="slidecontainer max-w-xl">
                <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('modals.kestrel_map.target_area_radius') }} (km)</label>
                <div class="flex items-center">
                  <input id="radius-slider" type="range" min="1" max="30"
                    class="slider"
                    v-model="radius"
                    @change="setRadius"
                    :disabled="props.viewOnly"
                  />
                  <span class="ml-2 text-sm text-gray-600">{{ radius }} km</span>
                </div>
              </div>
            </div>

            <!-- Village Toggle -->
            <div class="flex flex-col justify-center items-center">
              <div class="toggle-history-container">
                <div class="toggle-village-labels">
                  <span class="mr-2 text-sm font-medium text-gray-700">{{ t('modals.kestrel_map.village_labels') }}:</span>

                  <div class="flex items-center space-x-4">
                    <label class="inline-flex items-center">
                      <input class="form-radio" type="radio" id="villageNumber" value="number" v-model="labelType" @change="toggleVillageLabels">
                      <span class="ml-2 text-sm text-gray-700">{{ t('modals.kestrel_map.village_number') }}</span>
                    </label>
                    <label class="inline-flex items-center">
                      <input class="form-radio" type="radio" id="villageName" value="name" v-model="labelType" @change="toggleVillageLabels">
                      <span class="ml-2 text-sm text-gray-700">{{ t('modals.kestrel_map.village_name') }}</span>
                    </label>
                    <label class="inline-flex items-center">
                      <input class="form-radio" type="radio" id="noLabels" value="none" v-model="labelType" @change="toggleVillageLabels">
                      <span class="ml-2 text-sm text-gray-700">{{ t('modals.kestrel_map.none') }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Print Button -->
            <div class="flex items-center">
              <button
                v-if="isEditing"
                class="p-2 rounded-full bg-white shadow-md text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-200"
                @click="printMapAndDetails"
                :title="t('modals.kestrel_map.print_map')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Create/update Target Area buttons-->
          <div v-if="!props.viewOnly && targetAreasEnabled" class="flex justify-center space-x-4 p-4 bg-white border-t border-gray-200">
            <button
              v-if="!isEditing"
              class="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              @click="createTargetArea"
            >
              {{ t('modals.kestrel_map.create_target_area') }}
            </button>
            <div v-if="isEditing" class="flex items-center space-x-4">
              <button
                class="px-6 py-2 text-sm font-medium text-white bg-green-yellow hover:bg-brand-green-500 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-yellow transition-colors duration-200"
                @click="updateTargetArea"
              >
                {{ t('modals.kestrel_map.update_target_area') }}
              </button>
              <button
                class="p-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 border border-gray-300 hover:border-red-300"
                @click="deleteTargetArea"
                :title="t('modals.kestrel_map.delete_target_area')"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Batches List (only show on desktop when editing and not in manual survey mode) -->
    <div v-if="isEditing && !isMobile && !isManualSurveyMode" class="mt-2">
      <survey-batches
        :target-area-external-id="currentTargetAreaId || props.target_area_data[0].target_area_external_id"
        :training-center-name="props.target_area_data[0].name"
        :training-center-id="props.target_area_data[0].id"
        :preselected-villages="preselectedVillageCodes"
        :view-only="props.viewOnly"
        :mobile-view="false"
      />
    </div>

    <!-- Loading Overlay -->
    <div
      class="z-[9999] flex h-full w-full absolute left-0 top-0 justify-center items-center transition-opacity"
      v-if="isLoading"
    >
      <div class="absolute left-0 top-0 h-full w-full bg-gray-600 opacity-40"></div>
      <div class="text-[2.5rem]">{{ t('modals.kestrel_map.loading') }}</div>
    </div>
  </div>
</template>

<script setup>
  import { filter, difference, uniqBy, groupBy, forEach } from "lodash-es"
  import { ref, onMounted, computed, onUnmounted } from 'vue'
  import i18n from '@utils/i18n'
  import domtoimage from 'dom-to-image'
  import SurveyBatchKestrelSurveying from '@components/SurveyBatchKestrelSurveying'
  import FlashMessageService from '@services/FlashMessages'
  import SurveyBatches from '@components/SurveyBatches'
  import GlobalEventBus from '@services/GlobalEventBus'

  const { t } = i18n.global

  const props = defineProps({
    target_area_data: Array,
    viewOnly: {
      type: Boolean,
      default: false
    }
  })

  const kestrelMapContainer = ref(undefined)
  let radius = ref(5)
  let center = ref({ lat: 0, lng: 0 })
  let markers = ref([])
  let zoom = ref(1.8)
  let isEditing = ref(false)
  let currentTargetAreaId = ref(null)
  let isLoading = ref(true)

  let mapSession = undefined
  let targetAreaController = undefined
  let villageReportingInfo = ref([])
  let selectedVillages = ref([])
  let copiedCode = ref(null)
  let copyTimeout = ref(null)
  let printTimeout = ref(null)
  let preselectedVillageCodes = ref([])

  let isManualSurveyMode = ref(false)
  let surveyBatchUuid = ref(null)
  let surveyBatchName = ref('')
  let manualSurveyBatchData = ref({})

  // Add reactive state for pending surveys alert
  let hasPendingSurveys = ref(false)
  let pendingSurveysCount = ref(0)

  let hoveredPolygon = null
  let hoverTimer = null
  const HOVER_DELAY = 500 // 500ms delay before highlighting

  let tcLat = ref(undefined)
  let tcLng = ref(undefined)

  // PDF generation state
  let isPdfGenerating = ref(false)
  let pdfPollingInterval = ref(null)
  let pdfPollingTimeout = ref(null)
  let isPollingInProgress = ref(false)
  let generateSecurePdf = ref(false)

  // Mobile tab state
  const activeMobileTab = ref('map')
  const windowWidth = ref(window.innerWidth)
  const isMobile = computed(() => windowWidth.value < 768)

  // Sorting state
  const sortField = ref('name') // 'name', 'index'
  const sortDirection = ref('asc') // 'asc', 'desc'

  // Default village labels to not be shown
  const labelType = ref('none')

  // India, Bangladesh
  const countriesToSimplify = ref(['IND', 'BGD'])
  
  // Check if target areas are enabled for this training center
  const targetAreasEnabled = computed(() => {
    return props.target_area_data?.[0]?.target_areas_enabled === true
  })

  const sortedVillages = computed(() => {
    if (!selectedVillages.value || selectedVillages.value.length === 0) {
      return []
    }

    const villages = [...selectedVillages.value]

    villages.sort((a, b) => {
      let aValue, bValue

      if (sortField.value === 'name') {
        // If the village name is not available, use the index to sort
        // which will give us Named villages first, then Unnamed villages in index order
        aValue = a.villageName ? a.villageName.toLowerCase() : a.index
        bValue = b.villageName ? b.villageName.toLowerCase() : b.index
      } else if (sortField.value === 'index') {
        aValue = a.index
        bValue = b.index
      } else {
        return 0
      }

      if (sortDirection.value === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })

    return villages
  })

  function handleSort(field) {
    if (sortField.value === field) {
      // Toggle direction if same field
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    } else {
      // Set new field and default to ascending
      sortField.value = field
      sortDirection.value = 'asc'
    }
  }

  function getSortIcon(field) {
    if (sortField.value !== field) {
      return '↕' // Neutral sort icon
    }
    return sortDirection.value === 'asc' ? '↑' : '↓'
  }

  onMounted(async () => {
    window.addEventListener('resize', handleResize)
    try {
      let data = props.target_area_data.filter(x => x.lat != null)

      let duplicateTCs = difference(data, uniqBy(data, 'lat', 'long'), 'lat', 'long')
      let dupeys = groupBy(data, 'lat', 'long')

      if (data.length != 0) {
        let overallLat = 0
        let overallLong = 0

        data.forEach(i => {
          overallLat += parseFloat(i.lat)
          overallLong += parseFloat(i.long)
        })

        overallLat /= data.length
        overallLong /= data.length

        center.value = {
          lat: overallLat,
          lng: overallLong,
        }

        zoom.value = 2.5

        markers.value = data.map(tc => {
          let pos = {
            lat: parseFloat(tc.lat),
            lng: parseFloat(tc.long),
          }

          tcLat.value = pos.lat
          tcLng.value = pos.lng

          let dupes = duplicateTCs.filter(i => i.lat == tc.lat && i.long == tc.long)
          let label = dupes.length + 1

          let info = tc

          info.link = `${location.origin}/training_centers/${tc.id}`

          if (label >= 2) {
            forEach(dupeys, i => {
              if (info.lat == i[0].lat) {
                info.dupes = i
              }
            })
          }

          return {
            position: pos,
            info: info,
            label: label.toString()
          }
        })
      } else {
        center.value = { lat: 0, lng: 0 }
        zoom.value = 1
      }

      console.log("props.target_area_data[0]", props.target_area_data[0])

      // ************************************************** Map Initialization **************************************************
      mapSession = window.Kestrel.createMapSession(kestrelMapContainer.value, "kestrel-map-container")
      await mapSession.initialize({
        countryIso3Code: props.target_area_data[0].country_iso3_code,
        centerLat: center.value.lat,
        centerLng: center.value.lng,
        zoomLevel: zoom.value,
        kestrelApiUrl: props.target_area_data[0].kestrel_api_url,
        kestrelApiKey: props.target_area_data[0].kestrel_api_key,
        simplificationStack: countriesToSimplify.value.includes(props.target_area_data[0].country_iso3_code) ? { 4: 1 } : { 4: 0 },
        // automaticallyRenderVillageDots: true,
      })

      // Add Training Center coordinates to the map session as a blue circle
      let tcMarker = mapSession.renderCircle(
        tcLat.value,
        tcLng.value,
        300, // 300m radius
        {
          fillColor: 'blue',
          color: 'darkblue',
          weight: 2,
          fillOpacity: 0.7,
        }
      )

      mapSession.addZoomControl()

      targetAreaController = new Kestrel.TargetAreaController(mapSession, {
        targetAreaId: props.target_area_data[0].target_area_external_id ? props.target_area_data[0].target_area_external_id : undefined,
        description: () => "Target Area",
        orgCode: 'TTI',
        centerLatitude: center.value.lat,
        centerLongitude: center.value.lng,
        radius: radius.value,
        renderCircle: true,
        targetAreaBaseUrl: "/",
        shading: {
          type: "sparrow",
        },
        extraTargetAreaParameters: {
          training_center_id: props.target_area_data[0].training_center_id,
        },
      })

      // Register for selection changes
      targetAreaController.registerPolygonSelectionChangeInformer(handleSelectionChange)

      // Default village labels to not be shown
      targetAreaController.setVillageLabels('none')

      // If we have a target area ID, enable editing mode and fetch the batches that belong to this target area
      if (props.target_area_data[0].target_area_external_id) {
        try {
          isEditing.value = true
        } catch (err) {
          console.error('Error loading target area:', err)
          FlashMessageService.error(t('modals.kestrel_map.error_messages.loading_target_area'))
          isEditing.value = false
        }
      }

      // set currentTargetAreaId on load
      currentTargetAreaId.value = props.target_area_data[0].target_area_external_id

      // Hide loading overlay
      isLoading.value = false

      // Listen for exit manual survey mode event
      GlobalEventBus.subscribe('exit-manual-survey-mode', () => {
        toggleManualSurveyMode(false)
      })

      // Listen for village survey upserted events to update pending surveys count
      GlobalEventBus.subscribe('village-survey-upserted', () => {
        checkForPendingSurveys()
      })

      // Also check for pending surveys on mount
      checkForPendingSurveys()
    } catch (err) {
      console.error('Error initializing map:', err)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.initializing_map'))
      isLoading.value = false
    }
  })

  // Clean up timeout and event listener on component unmount
  onUnmounted(() => {
    if (copyTimeout.value) {
      clearTimeout(copyTimeout.value)
    }
    if (printTimeout.value) {
      clearTimeout(printTimeout.value)
    }
    if (pdfPollingInterval.value) {
      clearInterval(pdfPollingInterval.value)
    }
    if (pdfPollingTimeout.value) {
      clearTimeout(pdfPollingTimeout.value)
    }

    // Clear hover timer
    if (hoverTimer) {
      clearTimeout(hoverTimer)
      hoverTimer = null
    }

    // Destroy map session on unmount to prevent map loading issues
    console.log("Destroying map session on unmount:", mapSession.id)
    window.removeEventListener('resize', handleResize)
    window.Kestrel.destroyMapSession(mapSession.id)
  })

  function handleResize() {
    windowWidth.value = window.innerWidth
  }

  // Handle selection changes and process village data
  async function handleSelectionChange() {
    try {
      const polygons = targetAreaController.selectionGroup.selectedPolygons
      console.log("polygons", polygons)

      // Clear existing data to ensure no duplicates
      // This is processing the entire selectedPolygons object, so it needs to start fresh each time
      selectedVillages.value = []
      villageReportingInfo.value = []
      preselectedVillageCodes.value = []

      // Collect all villages first, then sort them for consistent ordering
      const villages = []
      let index = 0

      for (const [key, polygon] of Object.entries(polygons)) {
        const villageData = {
          id: polygon.id,
          index: ++index,
          villageAdmin4Code: polygon.admin4Code,
          villageName: polygon.admin4Name,
          villagePopulation: polygon.population,
          // surveyStatus is a string that sets color based on "has_churches"=green "has_christians"=yellow "none"='red' "null"=grey ... is "no_survey" grey too?
          villageShadingColor: polygon.surveyStatus === 'has_church' ? 'green' : polygon.surveyStatus === 'has_christians' ? 'yellow' : polygon.surveyStatus === 'none' ? 'red' : 'grey',
          // Store the polygon object for hover functionality
          polygon: polygon,
        }

        villages.push(villageData)
    }

      // Add villages to arrays (sorting will now be handled by computed property instead of always being sorted by name)
      selectedVillages.value = [...villages]
      villageReportingInfo.value = [...villages]
      preselectedVillageCodes.value = villages.map(village => village.id)

      console.log("selectedVillages", selectedVillages.value)
      console.log("villageReportingInfo", villageReportingInfo.value)
      console.log("preselectedVillageCodes", preselectedVillageCodes.value)
    } catch (error) {
      console.error('Error processing village data:', error)
    }
  }

  function setRadius () {
    if (!targetAreaController) { return }
    targetAreaController.setCenterRadius(radius.value)
  }

  function toggleVillageLabels () {
    if (!targetAreaController) { return }
    targetAreaController.setVillageLabels(labelType.value)
  }

  async function createTargetArea () {
    if (!targetAreaController) { return }

    try {
      const result = await targetAreaController.createNewTargetArea()

      isEditing.value = true
      currentTargetAreaId.value = targetAreaController.targetArea.externalId
      FlashMessageService.success(t('modals.kestrel_map.success_messages.target_area_created'))
    } catch (err) {
      console.error('Error creating target area:', err)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.creating_target_area'))
    }
  }

  async function updateTargetArea () {
    if (!targetAreaController) { return }
    try {
      await targetAreaController.updateExistingTargetArea()
      FlashMessageService.success(t('modals.kestrel_map.success_messages.target_area_updated'))
    } catch (err) {
      console.error('Error updating target area:', err)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.updating_target_area'))
    }
  }

  async function deleteTargetArea() {
    if (!currentTargetAreaId.value) { return }

    const confirm = window.confirm(t('modals.kestrel_map.confirm_delete_target_area'))
    if (!confirm) {
      return
    }

    try {
      isLoading.value = true

      // Soft delete the target area
      await targetAreaController.deactivateTargetArea()

      // Store the target area ID before clearing it
      const deletedTargetAreaId = currentTargetAreaId.value

      // Reset the component state
      isEditing.value = false
      currentTargetAreaId.value = null
      selectedVillages.value = []
      villageReportingInfo.value = []
      preselectedVillageCodes.value = []

      // Clear any pending surveys
      const currentUserId = window.gon?.current_user?.id || 'unknown'
      const manualSurveyKey = `manual_surveys_${currentUserId}_${deletedTargetAreaId}`
      localStorage.removeItem(manualSurveyKey)

      // Clear survey session batch
      localStorage.removeItem(`survey_session_batch_${currentUserId}`)

      // Hide the pending surveys alert
      hasPendingSurveys.value = false
      pendingSurveysCount.value = 0

      // Reset the target area controller to create mode
      if (targetAreaController) {
        // Reinitialize the controller to go back to create mode
        targetAreaController = new Kestrel.TargetAreaController(mapSession, {
          description: () => "Target Area",
          orgCode: 'TTI',
          centerLatitude: center.value.lat,
          centerLongitude: center.value.lng,
          radius: radius.value,
          renderCircle: true,
          targetAreaBaseUrl: "/",
          shading: {
            type: "sparrow",
          },
          extraTargetAreaParameters: {
            training_center_id: props.target_area_data[0].training_center_id,
          },
        })

        // Re-register for selection changes
        targetAreaController.registerPolygonSelectionChangeInformer(handleSelectionChange)
      }

      FlashMessageService.success(t('modals.kestrel_map.success_messages.target_area_deleted'))
    } catch (err) {
      console.error('Error deleting target area:', err)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.deleting_target_area'))
    } finally {
      isLoading.value = false
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      copiedCode.value = text
      if (copyTimeout.value) {
        clearTimeout(copyTimeout.value)
      }
      copyTimeout.value = setTimeout(() => {
        copiedCode.value = null
      }, 2000)
    })
  }

  function editTargetArea() {
    const trainingCenterId = props.target_area_data[0]?.training_center_id
    if (trainingCenterId) {
      window.location.href = `/training_centers/${trainingCenterId}/target_area`
    }
  }

  async function printMapAndDetails() {
    try {
      // Show loading state
      isLoading.value = true

      // Capture map as image using dom-to-image
      const mapElement = document.getElementById('map-container-to-print')
      const mapDataUrl = await domtoimage.toPng(mapElement)

      const printContainer = document.createElement('div')
      printContainer.id = 'print-container'

      // Create map section
      const mapSection = document.createElement('div')
      mapSection.style.cssText = 'page-break-after: always; padding: 20px; text-align: center; background-color: #ffffff; height: 100vh; display: flex; flex-direction: column; justify-content: center;'

      const mapSubtitle = document.createElement('h2')
      mapSubtitle.textContent = props.target_area_data[0].name
      mapSubtitle.style.cssText = 'margin-bottom: 20px; font-size: 18px; color: #666666;'

      const generatedOn = document.createElement('p')
      generatedOn.textContent = t('modals.kestrel_map.generated_on', { date: new Date().toLocaleDateString() })
      generatedOn.style.cssText = 'margin-bottom: 20px; font-size: 14px; color: #666666;'

      // Create canvas for the map
      const mapCanvas = document.createElement('canvas')
      const ctx = mapCanvas.getContext('2d')
      const img = new Image()

      // set canvas style and the image source to the dom-to-image PNG
      img.src = mapDataUrl
      mapCanvas.style.cssText = 'max-width: 700px; height: auto; margin: 0 auto; border-radius: 8px;'

      mapSection.appendChild(mapSubtitle)
      mapSection.appendChild(generatedOn)
      mapSection.appendChild(mapCanvas)

      // Create village details section
      const villageSection = document.createElement('div')
      villageSection.style.cssText = 'padding: 20px; background-color: #ffffff;'

      const villageTitle = document.createElement('h1')
      villageTitle.textContent = t('modals.kestrel_map.village_details')
      villageTitle.style.cssText = 'margin-bottom: 20px; font-size: 24px; color: #000000;'

      const villageSubtitle = document.createElement('h2')
      villageSubtitle.textContent = props.target_area_data[0].name
      villageSubtitle.style.cssText = 'margin-bottom: 20px; font-size: 18px; color: #666666;'

      // Create table container for margins
      const tableContainer = document.createElement('div')
      tableContainer.style.cssText = 'padding: 0 20px; margin-top: 20px;'

      // Create table
      const table = document.createElement('table')
      table.style.cssText = 'width: 100%; border: 2px solid #000000; color: #000000;'

      // Create table header
      const thead = document.createElement('thead')
      const headerRow = document.createElement('tr')
      const headers = [
        'Color',
        t('modals.kestrel_map.village_name'),
        t('modals.kestrel_map.population')
      ]

      // build the table headers
      headers.forEach(headerText => {
        const th = document.createElement('th')
        th.textContent = headerText
        th.style.cssText = 'padding: 12px; border-right: 1px solid #000000; border-bottom: 2px solid #000000; text-align: left; color: #000000;'
        headerRow.appendChild(th)
      })

      thead.appendChild(headerRow)
      table.appendChild(thead)

      // Create table body with the village data
      const tbody = document.createElement('tbody')
      villageReportingInfo.value.forEach((village, index) => {
        const row = document.createElement('tr')

        const indexCell = document.createElement('td')
        indexCell.textContent = village.index
        indexCell.style.cssText = `background-color: ${village.villageShadingColor}; width: 50px; padding: 12px; border-right: 1px solid #000000; border-bottom: ${index === villageReportingInfo.value.length - 1 ? 'none' : '1px solid #000000'}; color: #ffffff; font-weight: bold; text-align: center; text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000;`

        const nameCell = document.createElement('td')
        nameCell.textContent = village.villageName
        nameCell.style.cssText = `padding: 12px; border-right: 1px solid #000000; border-bottom: ${index === villageReportingInfo.value.length - 1 ? 'none' : '1px solid #000000'}; color: #000000;`

        const populationCell = document.createElement('td')
        populationCell.textContent = village.villagePopulation?.toLocaleString() || 'N/A'
        populationCell.style.cssText = `padding: 12px; border-right: 1px solid #000000; border-bottom: ${index === villageReportingInfo.value.length - 1 ? 'none' : '1px solid #000000'}; color: #000000;`

        row.appendChild(indexCell)
        row.appendChild(nameCell)
        row.appendChild(populationCell)
        tbody.appendChild(row)
      })

      table.appendChild(tbody)

      // Create footer with the total villages count
      const footer = document.createElement('p')
      footer.textContent = t('modals.kestrel_map.total_villages', { count: villageReportingInfo.value.length })
      footer.style.cssText = 'margin-top: 20px; font-style: italic; color: #000000;'

      // Assemble the print container
      villageSection.appendChild(villageTitle)
      villageSection.appendChild(villageSubtitle)
      tableContainer.appendChild(table)
      villageSection.appendChild(tableContainer)
      villageSection.appendChild(footer)

      printContainer.appendChild(mapSection)
      printContainer.appendChild(villageSection)

      // Add print styles
      const style = document.createElement('style')
      style.textContent = `
        @media print {
          @page {
            size: A4;
            margin: 1cm;
          }
          body > *:not(#print-container) {
            display: none !important;
          }
          #print-container {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            background: white !important;
            display: block !important;
          }
          #print-container > div {
            display: block !important;
            page-break-after: always !important;
            height: auto !important;
            min-height: 0 !important;
          }
          #print-container img {
            max-width: 90% !important;
            height: auto !important;
            margin: 0 auto !important;
          }
          #print-container table {
            width: 100% !important;
            border-collapse: separate !important;
            border-spacing: 0 !important;
          }
          #print-container th, #print-container td {
            padding: 8px !important;
          }
          #print-container td {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `

      // Add elements to document
      document.body.appendChild(printContainer)
      document.head.appendChild(style)

      // Wait for the image to load, then print
      img.onload = () => {
        // Set canvas size
        mapCanvas.width = img.width * 0.9
        mapCanvas.height = img.height * 0.9

        // Draw image on canvas
        ctx.drawImage(img, 0, 0, mapCanvas.width, mapCanvas.height)

        // Trigger print
        window.print()

        // Clean up temporary print container and styles after printing
        if (printTimeout.value) {
          clearTimeout(printTimeout.value)
        }

        printTimeout.value = setTimeout(() => {
          document.body.removeChild(printContainer)
          document.head.removeChild(style)
          isLoading.value = false
        }, 1000)
      }

    } catch (error) {
      console.error('Error printing map and details:', error)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.printing_failed'))
      isLoading.value = false
    }
  }

  function toggleManualSurveyMode(showConfirmation = true) {
    if (!isManualSurveyMode.value) {
      // Entering survey mode - create batch data for SurveyBatchKestrel
      const timestamp = new Date().toISOString().split('T')[0]
      surveyBatchName.value = `Manual Survey - ${props.target_area_data[0].name} - ${timestamp}`

      // Create batch data structure for SurveyBatchKestrel
      manualSurveyBatchData.value = {
        batch: {
          data: {
            batch_status: {
              batch: {
                name: surveyBatchName.value,
                state: 'ready_for_survey',
                external_uuid: null
              },
              village_surveys: []
            }
          }
        },
        churches: [],
        target_area: { id: currentTargetAreaId.value },
        training_center_id: props.target_area_data[0].training_center_id,
        country_iso3_code: props.target_area_data[0].country_iso3_code,
      }

      isManualSurveyMode.value = true
    } else {
      // Exiting manual survey mode - clear session batch and manual survey data from localStorage

      // Only show confirmation dialog if showConfirmation is true
      if (showConfirmation) {
        const confirm = window.confirm(t('modals.kestrel_map.confirm_exit_survey_mode'))
        if (!confirm) {
          return
        }
      }

      const currentUserId = window.gon?.current_user?.id || 'unknown'
      localStorage.removeItem(`survey_session_batch_${currentUserId}`)

      // Clear manual survey data for this target area
      const manualSurveyKey = `manual_surveys_${currentUserId}_${currentTargetAreaId.value}`
      localStorage.removeItem(manualSurveyKey)

      // Update pending surveys state
      checkForPendingSurveys()

      isManualSurveyMode.value = false
      surveyBatchUuid.value = null
      surveyBatchName.value = ''
      manualSurveyBatchData.value = {}
    }
  }

  function handleManualSurveyBatchCreated(batchData) {
    // Update our local state when a batch is created in the SurveyBatchKestrel component
    surveyBatchUuid.value = batchData.batchUuid
    surveyBatchName.value = batchData.batchName
  }

  async function submitManualSurveys() {
    try {
      // Dispatch event to trigger survey submission in SurveyBatchKestrel
      GlobalEventBus.dispatch('submit-manual-surveys')
    } catch (error) {
      console.error('Error submitting manual surveys:', error)
      FlashMessageService.error('Failed to submit surveys: ' + error.message)
    }
  }

  // Check for pending surveys in localStorage
  function checkForPendingSurveys() {
    try {
      const currentUserId = window.gon?.current_user?.id || 'unknown'
      const manualSurveyKey = `manual_surveys_${currentUserId}_${currentTargetAreaId.value}`
      const manualSurveyData = localStorage.getItem(manualSurveyKey)

      if (manualSurveyData) {
        const parsed = JSON.parse(manualSurveyData)
        if (parsed.surveys && parsed.surveys.length > 0) {
          hasPendingSurveys.value = true
          pendingSurveysCount.value = parsed.surveys.length
          return
        }
      }

      hasPendingSurveys.value = false
      pendingSurveysCount.value = 0
    } catch (error) {
      console.error('Error checking for pending surveys:', error)
      hasPendingSurveys.value = false
      pendingSurveysCount.value = 0
    }
  }

  function clearPendingSurveys() {
    const confirm = window.confirm(t('modals.kestrel_map.confirm_discard_surveys'))
    if (!confirm) {
      return
    }

    try {
      const currentUserId = window.gon?.current_user?.id || 'unknown'
      const manualSurveyKey = `manual_surveys_${currentUserId}_${currentTargetAreaId.value}`
      localStorage.removeItem(manualSurveyKey)

      // Update pending surveys state
      checkForPendingSurveys()

      FlashMessageService.success(t('modals.kestrel_map.surveys_discarded'))
    } catch (error) {
      console.error('Error clearing pending surveys:', error)
      FlashMessageService.error('Failed to clear pending surveys: ' + error.message)
    }
  }

  function handleVillageHoverIn(village) {
    // Clear any existing timer
    if (hoverTimer) {
      clearTimeout(hoverTimer)
      hoverTimer = null
    }

    // Set a timer to highlight after delay
    hoverTimer = setTimeout(() => {
      try {
        if (village.polygon) {
          // Store reference to remove later
          hoveredPolygon = village.polygon

          // Store original color if not already stored
          if (!hoveredPolygon.originalColor) {
            hoveredPolygon.originalColor = village.polygon.style.fillColor
          }
          
          // Highlight the polygon light blue
          hoveredPolygon.setFillColor('#3885e9')
          
          // Show village name on hover only when labelType is "none"
          if (labelType.value === 'none') {
            hoveredPolygon.manuallyTriggerHoverIn()
          }
        }
      } catch (error) {
        console.error('Error highlighting polygon on hover:', error)
      }
    }, HOVER_DELAY)
  }

  function handleVillageHoverOut(village) {
    // Clear the timer if mouse leaves before delay
    if (hoverTimer) {
      clearTimeout(hoverTimer)
      hoverTimer = null
    }

    // Reset polygon styling if it was highlighted
    if (hoveredPolygon) {
      try {
        // Reset to original color
        const originalColor = hoveredPolygon.originalColor || village.polygon.style.fillColor
        hoveredPolygon.setFillColor(originalColor)
        
        // Hide village name on hover out only when labelType is "none"
        if (labelType.value === 'none') {
          hoveredPolygon.manuallyTriggerHoverOut()
        }
      } catch (error) {
        console.error('Error removing hover highlight:', error)
        // If there's an error, just clear the reference
        hoveredPolygon = null
      }
      hoveredPolygon = null
    }
  }

  async function generatePDF() {
    if (!currentTargetAreaId.value) {
      FlashMessageService.error(t('modals.kestrel_map.error_messages.no_target_area'))
      return
    }

    // Clear any existing polling interval and timeout
    if (pdfPollingInterval.value) {
      clearTimeout(pdfPollingInterval.value)
      pdfPollingInterval.value = null
    }
    if (pdfPollingTimeout.value) {
      clearTimeout(pdfPollingTimeout.value)
      pdfPollingTimeout.value = null
    }
    isPollingInProgress.value = false

    try {
      isPdfGenerating.value = true

      // Start PDF generation request
      const response = await fetch(`/target_areas/${currentTargetAreaId.value}/generate_pdf.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          restricted: generateSecurePdf.value
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate PDF')
      }

      console.log('PDF generation response:', data)

      // Extract the pdf_job_id from the response
      const pdfJobId = data.pdf_job_id

      if (!pdfJobId) {
        isPdfGenerating.value = false
        FlashMessageService.error(t('modals.kestrel_map.error_messages.no_polling_url'))
        return
      }

      // PDF not ready yet, start polling
      FlashMessageService.success(t('modals.kestrel_map.success_messages.pdf_generating'))

      // Set a 30-second timeout
      pdfPollingTimeout.value = setTimeout(() => {
        if (pdfPollingInterval.value) {
          clearTimeout(pdfPollingInterval.value)
          pdfPollingInterval.value = null
        }
        isPollingInProgress.value = false
        isPdfGenerating.value = false
        FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_timeout'))
      }, 30000) // 30 seconds

      // Sequential polling function - waits for each request to finish before starting the next
      const pollStatus = async () => {
        // Prevent overlapping calls
        if (isPollingInProgress.value) {
          return
        }

        isPollingInProgress.value = true

        try {
          const pollResponse = await fetch(`/target_areas/${currentTargetAreaId.value}/check_pdf_status.json?pdf_job_id=${pdfJobId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            }
          })

          if (!pollResponse.ok) {
            throw new Error(`Failed to check PDF status: ${pollResponse.status}`)
          }

          const pollData = await pollResponse.json()
          console.log('Polling response:', pollData)

          // Check if PDF generation is complete
          if (pollData.cached_resource_request?.cached_resource?.download_url) {
            // Success! Clear timeout and stop polling
            clearTimeout(pdfPollingTimeout.value)
            pdfPollingTimeout.value = null
            if (pdfPollingInterval.value) {
              clearTimeout(pdfPollingInterval.value)
              pdfPollingInterval.value = null
            }
            isPollingInProgress.value = false
            isPdfGenerating.value = false

            FlashMessageService.success(t('modals.kestrel_map.success_messages.pdf_ready'))
            // Open the PDF in a new tab
            console.log('Download URL:', pollData.cached_resource_request.cached_resource.download_url)
            window.open(pollData.cached_resource_request.cached_resource.download_url, '_blank')
            return
          } else if (pollData.state === 'failed') {
            // Error! Clear timeout and stop polling
            clearTimeout(pdfPollingTimeout.value)
            pdfPollingTimeout.value = null
            if (pdfPollingInterval.value) {
              clearTimeout(pdfPollingInterval.value)
              pdfPollingInterval.value = null
            }
            isPollingInProgress.value = false
            isPdfGenerating.value = false

            FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_generation_failed', {
              error: pollData.error_message || 'Unknown error'
            }))
            return
          }

          // Still processing - schedule next poll after current request finishes
          isPollingInProgress.value = false
          pdfPollingInterval.value = setTimeout(pollStatus, 1000) // Poll again after 1 second
        } catch (pollError) {
          console.error('Error polling PDF status:', pollError)
          clearTimeout(pdfPollingTimeout.value)
          pdfPollingTimeout.value = null
          if (pdfPollingInterval.value) {
            clearTimeout(pdfPollingInterval.value)
            pdfPollingInterval.value = null
          }
          isPollingInProgress.value = false
          isPdfGenerating.value = false
          FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_polling_failed'))
        }
      }

      // Start the first poll
      pollStatus()
    } catch (error) {
      console.error('Error generating PDF:', error)
      FlashMessageService.error(t('modals.kestrel_map.error_messages.pdf_generation_failed', { error: error.message }))
      isPdfGenerating.value = false
      if (pdfPollingInterval.value) {
        clearTimeout(pdfPollingInterval.value)
        pdfPollingInterval.value = null
      }
      if (pdfPollingTimeout.value) {
        clearTimeout(pdfPollingTimeout.value)
        pdfPollingTimeout.value = null
      }
      isPollingInProgress.value = false
    }
  }


</script>

<style scoped>
  .slidecontainer {
    width: 100%;
  }

  .slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    background: #e2e8f0;
    outline: none;
    border-radius: 4px;
    transition: opacity .2s;
  }

  .slider:disabled {
    background: #cbd5e1;
  }

  .slider:hover:not(:disabled) {
    opacity: 1;
  }

  .slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: #3b82f6;
    cursor: pointer;
    border-radius: 50%;
    transition: background-color .2s;
  }

  .slider:disabled::-webkit-slider-thumb {
    background: #94a3b8;
    cursor: not-allowed;
  }

  .slider:not(:disabled)::-webkit-slider-thumb:hover {
    background: #2563eb;
  }

  .slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: #3b82f6;
    cursor: pointer;
    border-radius: 50%;
    border: none;
    transition: background-color .2s;
  }

  .slider:disabled::-moz-range-thumb {
    background: #94a3b8;
    cursor: not-allowed;
  }

  .slider:not(:disabled)::-moz-range-thumb:hover {
    background: #2563eb;
  }

  .form-radio {
    @apply text-blue-600 focus:ring-blue-500 h-4 w-4;
  }

  .circle {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 9999px;
    font-size: 0.85em;
    font-weight: 600;
    color: #fff;
    box-shadow: 0 0 0 1px #e5e7eb;
    line-height: 1;
  }

  .circle-number {
    text-shadow:
      -1px -1px 0 #000,
       1px -1px 0 #000,
      -1px  1px 0 #000,
       1px  1px 0 #000;
    font-weight: 700;
  }
</style>


